// src/components/Payments.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useCompetition } from '../competition/CompetitionContext';
import './Payments.css';

const _host = 'chappie-demo.novosense.africa';
const _company = 'wdsf';

const Payments = () => {
  const { selectedCompetition } = useCompetition();
  const [dancers, setDancers] = useState([]);
  const [filteredDancers, setFilteredDancers] = useState([]);
  const [events, setEvents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [expandedEvents, setExpandedEvents] = useState(new Set()); // Track which events are expanded

  // Fetch events data
  const fetchEvents = async () => {
    try {
      const response = await fetch(`https://${_host}:6003/api/${_company}/events`);
      if (!response.ok) {
        throw new Error('Failed to fetch events');
      }
      const data = await response.json();
      setEvents(data || []);
    } catch (err) {
      console.error('Error fetching events:', err);
    }
  };

  // Helper function to get all events for a dancer (copied from CouplesList)
  const getDancersList = useCallback((event) => {
    if (!event) return [];

    // Handle both JSON strings and arrays
    if (typeof event.dancers === 'string') {
      try {
        return JSON.parse(event.dancers);
      } catch (e) {
        console.error('Failed to parse dancers JSON:', e);
        return [];
      }
    }

    // Handle nested heats structure
    if (event.heats && Array.isArray(event.heats)) {
      const allDancers = [];
      event.heats.forEach(heat => {
        if (heat.dancers) {
          if (typeof heat.dancers === 'string') {
            try {
              const parsedDancers = JSON.parse(heat.dancers);
              allDancers.push(...parsedDancers);
            } catch (e) {
              console.error('Failed to parse heat dancers JSON:', e);
            }
          } else if (Array.isArray(heat.dancers)) {
            allDancers.push(...heat.dancers);
          }
        }
      });
      return allDancers;
    }

    return Array.isArray(event.dancers) ? event.dancers : [];
  }, []);

  // Helper function to get ALL events for a dancer
  const getAllDancerEvents = useCallback((dancer) => {
    if (!events || events.length === 0) return 'No events';

    const clothNumber = dancer.clothNumber;
    const dancerId = dancer.id;

    // Find all events where this dancer is allocated
    const allocatedEvents = events.filter(event => {
      if (!event) return false;

      const dancers = getDancersList(event);
      if (!Array.isArray(dancers)) return false;

      return dancers.some(eventDancer => {
        const dancerClothNumber = eventDancer.clothNumber || eventDancer.cloth_number;
        const eventDancerId = eventDancer.id || eventDancer.dancerId;

        // Match by both cloth number and ID for accuracy
        return (dancerClothNumber === clothNumber) || (eventDancerId === dancerId);
      });
    });

    if (allocatedEvents.length === 0) {
      return dancer.eventName || 'No events';
    }

    // Return ALL allocated event names as comma-separated list
    return allocatedEvents.map(event => event.name).join(', ');
  }, [events, getDancersList]);

  // Toggle event expansion for a dancer
  const toggleEventExpansion = (dancerId) => {
    setExpandedEvents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(dancerId)) {
        newSet.delete(dancerId);
      } else {
        newSet.add(dancerId);
      }
      return newSet;
    });
  };

  // Fetch dancers with their club and event information
  const fetchDancers = async () => {
    setLoading(true);
    setError(null);

    try {
      let url = `https://${_host}:6003/api/${_company}/payments`;

      // Add competitionId filter if available
      if (selectedCompetition && selectedCompetition.id) {
        url += `?competitionId=${selectedCompetition.id}`;
      }

      console.log('🔍 Fetching payments data from:', url);

      const response = await fetch(url);
      console.log('🔍 Response status:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('🔍 Response error:', errorText);
        throw new Error(`Failed to fetch dancers data: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('🔍 Payments data received:', data?.length || 0, 'dancers');

      setDancers(data || []);
      setFilteredDancers(data || []);
    } catch (err) {
      console.error('🔍 Error fetching payments data:', err);
      setError(err.message || 'Failed to fetch dancer data');
    } finally {
      setLoading(false);
    }
  };

  // Toggle payment status for a dancer
  const togglePaymentStatus = async (dancerId, currentStatus) => {
    setUpdatingId(dancerId);

    try {
      const response = await fetch(`https://${_host}:6003/api/${_company}/couples/${dancerId}/payment-status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          payment_status: !currentStatus
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update payment status');
      }

      // Update local state to reflect the change
      setDancers(prevDancers =>
        prevDancers.map(dancer =>
          dancer.id === dancerId
            ? { ...dancer, paymentStatus: !currentStatus }
            : dancer
        )
      );
      
      // Update filtered dancers as well
      setFilteredDancers(prevFiltered =>
        prevFiltered.map(dancer =>
          dancer.id === dancerId
            ? { ...dancer, paymentStatus: !currentStatus }
            : dancer
        )
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdatingId(null);
    }
  };

  // Filter dancers based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredDancers(dancers);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = dancers.filter(dancer =>
      // Search by cloth number
      (dancer.clothNumber && dancer.clothNumber.toString().toLowerCase().includes(term)) ||
      // Search by lead name
      (dancer.leadName && dancer.leadName.toLowerCase().includes(term)) ||
      // Search by follow name
      (dancer.followName && dancer.followName.toLowerCase().includes(term)) ||
      // Search by club name
      (dancer.clubName && dancer.clubName.toLowerCase().includes(term)) ||
      // Search by country
      (dancer.country && dancer.country.toLowerCase().includes(term)) ||
      // Search by province
      (dancer.province && dancer.province.toLowerCase().includes(term)) ||
      // Search by event name
      (dancer.eventName && dancer.eventName.toLowerCase().includes(term))
    );

    setFilteredDancers(filtered);
  }, [searchTerm, dancers]);

  // Calculate payment statistics
  const stats = useMemo(() => {
    const total = dancers.length;
    const paid = dancers.filter(d => d.paymentStatus).length;
    const unpaid = total - paid;
    const paidPercentage = total > 0 ? ((paid / total) * 100).toFixed(1) : 0;

    return { total, paid, unpaid, paidPercentage };
  }, [dancers]);

  // Fetch data when component mounts or competition changes
  useEffect(() => {
    fetchDancers();
    fetchEvents(); // Fetch events to enable multi-event display
  }, [selectedCompetition]);

  return (
    <div className="payments-container">
      {/* Fixed Header - Not Scrollable */}
      <div className="payments-header">
        <h2>Payments Management</h2>
        {selectedCompetition && (
          <div className="competition-info">
            Competition: {selectedCompetition.name}
          </div>
        )}
      </div>

      {/* Fixed Stats and Search - Not Scrollable */}
      <div className="fixed-content">
        {/* Payment Statistics */}
        <div className="payment-stats">
          <div className="stat-card total">
            <h3>{stats.total}</h3>
            <p>Total Dancers</p>
          </div>
          <div className="stat-card paid">
            <h3>{stats.paid}</h3>
            <p>Paid ({stats.paidPercentage}%)</p>
          </div>
          <div className="stat-card unpaid">
            <h3>{stats.unpaid}</h3>
            <p>Unpaid</p>
          </div>
        </div>

        {/* Search Bar - Full Width */}
        <div className="search-container">
          <div className="search-input-wrapper">
            <input
              type="text"
              placeholder="Search by cloth number, name, club, country, province, or event..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="clear-search-btn"
              >
                ×
              </button>
            )}
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="error-message">
            {error}
            <button onClick={() => setError(null)} className="dismiss-error">×</button>
          </div>
        )}
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="loading-state">Loading dancer data...</div>
      ) : (
        /* Single Table Container with Fixed Header */
        <div className="table-wrapper">
          <div className="dancers-table-container">
            <table className="dancers-table">
              <thead>
                <tr>
                  <th>Cloth Number</th>
                  <th>Lead Name</th>
                  <th>Follow Name</th>
                  <th>Club/Studio</th>
                  <th>Country</th>
                  <th>Province</th>
                  <th>Event</th>
                  <th>Payment Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredDancers.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="no-data">
                      {searchTerm ? 'No dancers found matching your search.' : 'No dancers registered.'}
                    </td>
                  </tr>
                ) : (
                  filteredDancers.map((dancer) => (
                    <tr
                      key={dancer.id}
                      className={`dancer-row ${dancer.paymentStatus ? 'paid' : 'unpaid'}`}
                    >
                      <td className="cloth-number">{dancer.clothNumber}</td>
                      <td className="lead-name">{dancer.leadName || '-'}</td>
                      <td className="follow-name">{dancer.followName || '-'}</td>
                      <td className="club-name">{dancer.clubName || '-'}</td>
                      <td className="country">{dancer.country || '-'}</td>
                      <td className="province">{dancer.province || '-'}</td>
                      <td className="event-name">
                        <span
                          className={`event-name-badge ${expandedEvents.has(dancer.id) ? 'expanded' : ''}`}
                          onClick={() => toggleEventExpansion(dancer.id)}
                          title={`Click to ${expandedEvents.has(dancer.id) ? 'collapse' : 'expand'} events`}
                        >
                          {expandedEvents.has(dancer.id)
                            ? getAllDancerEvents(dancer)
                            : getAllDancerEvents(dancer).substring(0, 40) + (getAllDancerEvents(dancer).length > 40 ? '...' : '')
                          }
                        </span>
                      </td>
                      <td className={`payment-status ${dancer.paymentStatus ? 'paid' : 'unpaid'}`}>
                        <span className="status-indicator"></span>
                        {dancer.paymentStatus ? 'Paid' : 'Not Paid'}
                      </td>
                      <td className="actions">
                        <button
                          onClick={() => togglePaymentStatus(dancer.id, dancer.paymentStatus)}
                          disabled={updatingId === dancer.id}
                          className={`toggle-payment-btn ${dancer.paymentStatus ? 'mark-unpaid' : 'mark-paid'}`}
                        >
                          {updatingId === dancer.id ? (
                            'Updating...'
                          ) : dancer.paymentStatus ? (
                            'Mark as Unpaid'
                          ) : (
                            'Mark as Paid'
                          )}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payments;